import { useStore } from '@nanostores/react';
import { accountStore, authStore } from './store';
import type { AccountPublicSettings } from './types';

/**
 * Account-level, customer-controlled opt-in settings, stored in
 * `accounts.public_settings`. Distinct from feature flags (staff/rollout
 * control): these are toggles the account sets for itself. The account-setting
 * analogue of `AccountFeatureFlag`.
 */
export const AccountSetting = {
  AiEnabled: 'ai_enabled',
  UseGlobalAiEndpoint: 'use_global_ai_endpoint',
  ConsentRecords: 'consent_records',
} as const satisfies Record<string, keyof AccountPublicSettings>;

export type AccountSetting = (typeof AccountSetting)[keyof typeof AccountSetting];

/**
 * Whether the given account setting is on. Subscribes to the account store, so
 * consumers react to changes within a session (e.g. after toggling AI in
 * settings). Missing settings are treated as off. Mirrors `useFeatureFlag`.
 */
export const useAccountSetting = (setting: AccountSetting): boolean => {
  const account = useStore(accountStore);
  return account?.public_settings?.[setting] === true;
};

/** Non-hook read of an account setting, for predicate contexts. Mirrors `hasFeatureFlag`. */
export const hasAccountSetting = (setting: AccountSetting): boolean => {
  return authStore.get()?.account?.public_settings?.[setting] === true;
};
